import {Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';

import {ExplorerItem, TreeNodeInfo} from '@catui/types';
import {EntitySchemaService} from "@catcore/services/entity-schema.service";
import {Observable} from "rxjs";
import {EntityData, EntityProperty, EntitySchema} from "@catmodules/model-editor/types";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";
import {DisplayTypes} from "@catui/components/panel/types";
import {ResourceHandler} from "@catfeatures/types";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {AddToRelationComponent} from "@catmodules/model-editor/components/add-to-relation.component";
import * as lodash from "lodash";
import {NotificationsService} from "@catcore/services";
import {Message} from "@catui/components/alert/types";
import {ROLE, UserService} from "@catcore/services/user.service";
import {CatUser} from "@catcore/types/api-v2";
import Object from 'core-js/es6/object';

const componentBaseName = 'model-editor';
const selectorName = 'cat-' + componentBaseName;
const defaultClassNames = [selectorName].join(' ');
const defaultBreadcrumb = ['Model Editor'];

@Component({
    selector: 'cat-model-editor',
    templateUrl: './model-editor.component.html',
    styleUrls: ['./model-editor.component.scss']
})
export class ModelEditorComponent implements OnInit, OnDestroy {
    @HostBinding('class') classNames = defaultClassNames;

    objectKeys = Object.keys;
    objectValues = Object.values;
    cloneDeep = lodash.cloneDeep;
    panelDisplayType = DisplayTypes;

    titleBreadcrumbs: string[] = defaultBreadcrumb;
    doDisplayAddOrCreate: boolean = true;
    create: boolean = false;

    user: CatUser;
    ROLES = ROLE;

    // Model / schema level
    protected explorerItemsModels: ExplorerItem[] = [];
    activeExplorerItemModel: ExplorerItem;
    private modelSchemas: EntitySchema[] = [];
    private allSchemas: EntitySchema[] = [];
    protected selectedSchema: EntitySchema;

    // Entity level
    protected explorerItemsEntities: ExplorerItem[] = [];
    activeExplorerItemEntity: ExplorerItem;
    protected entityData: EntityData;
    private selectedEntityId: number = null;
    protected unsupportedFields: Set<EntityProperty> = new Set<EntityProperty>();

    private routeData$: Observable<any>;
    currentResource$: Store<any>;

    private resourceServiceColl: ResourceHandler;
    private resourceServiceItem: ResourceHandler;

    public form: FormGroup;
    public initialForm: FormGroup;

    public messages: Message[] = [];
    public messagesForRelations: Message[] = [];

    constructor(private router: Router,
                private route: ActivatedRoute,
                private genericResourceService: GenericResourceService,
                private entitySchemaService: EntitySchemaService,
                private userService: UserService,
                private modalService: ModalService,
                private notificationService: NotificationsService,
    ) {
        this.routeData$ = this.route.data;
        this.user = userService.getCurrentUser();
        userService.getUser().subscribe((user: CatUser) => {
          this.user = user;
        });

        const selectedModel: string = this.route.snapshot.url.length > 0 ? this.route.snapshot.url[0].path : null;
        if (!selectedModel) {
            this.resetModel();
        }
        if (this.route.snapshot.url.length > 1) {
            const segment: string = this.route.snapshot.url[1].path;
            if (segment === 'create') {
                this.create = true;
            } else {
                this.selectedEntityId =  Number.parseInt(segment);
            }
        }
        if (!this.selectedEntityId) {
            this.resetEntity();
        }
        // Store model schemas, init explorer items
        if (this.modelSchemas.length == 0) {
            let rawSchemas = this.entitySchemaService.getRawSchema();
            const self = this;
            Object.keys(rawSchemas).sort().forEach(function (resourceUri) {
                const schema: any = rawSchemas[resourceUri];
                if (schema.model_level === 'entity') {
                    self.modelSchemas[resourceUri] = schema;
                    self.allSchemas[resourceUri] = schema;

                    const explorerItem = self.createExplorerItemModel(resourceUri);
                    self.explorerItemsModels.push(explorerItem);

                    if (selectedModel != null && selectedModel == resourceUri) {
                        setTimeout(() => {
                            self.onActivateItemModel({
                                item: explorerItem,
                                nodeLevel: 0,
                                isCustom: false,
                                processed: false
                            }, true);
                        });
                    }
                } else {
                    self.allSchemas[resourceUri] = schema;
                }
            });
            if (this.create) {
                setTimeout(() => { this.onAddOrCreate(null); });
            }
        }
    }

    private resetModel(): void {
        this.selectedSchema = null;
        this.entityData = null;
    }

    private resetEntity(): void {
        this.selectedEntityId = null;
        this.activeExplorerItemEntity = null;
        this.unsupportedFields = new Set<EntityProperty>();
        this.entityData = null;
        this.messages = [];
        this.messagesForRelations = [];
    }

    ngOnInit() {
        this.resetActiveExplorerItemModel();
        this.resetActiveExplorerItemEntity();
        this.updateTitleBreadcrumbs();
    }

    ngOnDestroy() {

    }

    onActivateItemModel(nodeInfo: TreeNodeInfo, noEntityReset: boolean = false) {
        this.selectedSchema = this.modelSchemas[nodeInfo.item.value];
        if (!this.selectedSchema.operations.collection.includes("get")) {
            this.notificationService.error(
                'GET method is not supported!',
                'Add GET method to collection operations.'
            );
            return;
        }
        if (
            !this.user.hasRole(ROLE.MODEL_EDIT) ||
            !this.selectedSchema.operations.collection.includes("post")
        ) {
            this.doDisplayAddOrCreate = false;
        }

        this.activeExplorerItemModel = nodeInfo.item;
        this.router.navigate(['model-editor', nodeInfo.item.value]);
        if (!noEntityReset) this.resetEntity();
        this.create = false;
        this.updateEntityExplorer();
    }

    private checkMethodsAndAddMessages() {
        // Check ROLE
        if (!this.user.roles.includes(ROLE.MODEL_EDIT)) {
            this.messages.push({
                title: "Read-only mode! Editing of model entities not possible!",
                description: "You don't have the required ROLE " + ROLE.MODEL_EDIT + ". Contact an administrator for a fix."
            });
        }

        // Entity methods
        const ms = [
            { action: "creation", method: "POST", ops: "collection" },
            { action: "updating", method: "PUT", ops: "item" },
            { action: "deletion", method: "DELETE", ops: "item" }
        ];
        ms.forEach((m: { action: string, method: string, ops: string }) => {
            if (!this.entityData.schema.operations[m.ops].includes(m.method.toLowerCase())) {
                this.messages.push({
                    title: "Entity " + m.action + " is not possible!",
                    description: m.method + " method is not supported. Add it to " + m.ops + " operations for a fix."
                });
            }
        });

        // Relation methods
        this.entityData.relations.forEach( (prop: EntityProperty) => {
            const relUri: string = this.getValueUri(prop);
            if (relUri) {
                const relSchema: EntitySchema = this.allSchemas[relUri];
                if (!relSchema.operations.collection.includes("get")) {
                    if (!this.messagesForRelations[relUri]) this.messagesForRelations[relUri] = [];
                    this.messagesForRelations[relUri].push({
                        title: "Relation entities can not be retrieved!",
                        description:
                            "Entity " + this.createLabel(relUri) + " doesn't support GET method. " +
                            "Add it to its collection operations for a fix."
                    });
                }
                if (!prop.writable) {
                    if (!this.messagesForRelations[relUri]) this.messagesForRelations[relUri] = [];
                    this.messagesForRelations[relUri].push({
                        title: "Relation is not writable!",
                        description:
                            "Relation " + prop.name + " doesn't have the _write serialization group. " +
                            "Add it to its @Groups annotation for a fix."
                    });
                }
            }
        });
    }
    onActivateItemEntity(nodeInfo: TreeNodeInfo) {
        if (!this.selectedSchema.operations.item.includes("get")) {
            this.notificationService.error(
                'GET method is not supported!',
                'Add GET method to item operations.'
            );
            return;
        }
        this.resetEntity();
        this.create = false;
        this.activeExplorerItemEntity = nodeInfo.item;
        const resourceUri = this.activeExplorerItemModel.value;
        this.router.navigate(['model-editor', resourceUri, nodeInfo.item.value.id]);

        // Fetch detailed entity
        const self = this;
        this.fetchResources((resource: any) => {
            if (resource) {
                self.updateTitleBreadcrumbs();

                // create form
                this.createEntityDataAndForm(resource);
            }
        },true);
    }

    createDropdownOptions(choices: string[]) {
        let options = [];
        choices.forEach((choice: string) => {
            options.push({label: choice == null ? '-null-' : choice, value: choice});
        });
        return options;
    }
    private createEntityDataAndForm(entity: object = {}) {
        // let formControls: Object = {};
        let formControls: {[key: string]: AbstractControl;} = {};
        let fields: EntityProperty[] = [];
        let relations: EntityProperty[] = [];
        // this.unsupportedFields = Object.keys(this.selectedSchema.properties);
        this.unsupportedFields = new Set<EntityProperty>(Object.values(this.selectedSchema.properties));
        Object.values(this.selectedSchema.properties).forEach((prop: EntityProperty) => {
            // if (prop.type.builtin_type != 'object') {
            if (this.create && prop.name == 'id') {
                this.unsupportedFields.delete(prop);
                return;
            }
            if (!this.create && !prop.readable) {
                this.unsupportedFields.delete(prop);
                return;
            }
            let control: FormControl|FormArray;
            let isRelation : boolean = this.isRelation(prop);
            if (prop.type.collection) {
                let controls: FormControl[] = [];
                let entityValue = isRelation ?
                    (entity['_links'] && entity['_links'][prop.name] ? entity['_links'][prop.name] : undefined) :
                    (entity[prop.name] ? entity[prop.name] : undefined);
                if (entityValue) {
                    (entityValue as Array<any>).forEach((val) => {
                        controls.push(this.createFormControl(prop, val));
                    });
                }
                control = new FormArray(controls);
            } else {
                let entityValue = isRelation ?
                    (entity['_links'] && entity['_links'][prop.name] ? entity['_links'][prop.name] : undefined) :
                    (entity.hasOwnProperty(prop.name) ? entity[prop.name] : undefined);
                control = this.createFormControl(prop, entityValue);
            }
            formControls[prop.name] = control;
            if (!isRelation) {
                fields.push(prop);
            } else {
                relations.push(prop);
            }
            this.unsupportedFields.delete(prop);
        });
        this.entityData = {
            schema: this.selectedSchema,
            data: entity,
            fields: this.sortFields(fields),
            relations: relations
        };

        // Create messages
        this.checkMethodsAndAddMessages();

        this.form = new FormGroup(formControls);
        this.initialForm = this.cloneDeep(this.form);

    }
    private sortFields(fields: EntityProperty[]): EntityProperty[] {
        return fields.sort((a: EntityProperty, b: EntityProperty) => {
            let fieldScoreDecision : number = Math.max(Math.min(this.calcFieldScore(b) - this.calcFieldScore(a), 1), -1);
            return fieldScoreDecision != 0 ? fieldScoreDecision : a.name.localeCompare(b.name);
        });
    }

    private calcFieldScore(field: EntityProperty): number {
        let score: number = 0;
        switch (field.type.builtin_type) {
            case 'string': score += 9000; break;
            case 'int': score += 6000; break;
            case 'bool': score += 3000; break;
        }
        if (!field.writable) score += 500;
        return score;
    }


    validationMessages = {
        default: {
            required: "This field is required!",
            pattern: (error) => { return "This field must match the pattern '" + error.requiredPattern + "'!"; },
        },
        overrides: {
            "network-interfaces": {
                ipAdress: {
                    pattern: "no valid IP address!"
                }
            }
        }
    };

    private getValidationMessages(prop: EntityProperty) : Array<string> {
        const messages: Array<string> = [];
        const control: AbstractControl = this.form.get(prop.name);
        Object.keys(control.errors).forEach( (errorType: string) => {
            if (control && control.hasError(errorType) && (control.dirty || control.touched)) {
                let message: string;
                if (
                    this.validationMessages.overrides.hasOwnProperty(this.activeExplorerItemModel.value) &&
                    this.validationMessages.overrides[this.activeExplorerItemModel.value].hasOwnProperty(prop.name) &&
                    this.validationMessages.overrides[this.activeExplorerItemModel.value][prop.name].hasOwnProperty(errorType)
                ) {
                    message = this.validationMessages.overrides[this.activeExplorerItemModel.value][prop.name][errorType];
                } else if (this.validationMessages.default.hasOwnProperty(errorType)) {
                    message = this.validationMessages.default[errorType];
                } else {
                    message = "No validation message defined for type '" + errorType + "'!";
                }
                if (typeof message == "function") {
                    message = message(control.getError(errorType));
                }
                messages.push(message);
            }
        });

        return messages;
    }


    private selectValidators(prop: EntityProperty) {
        let validators = [];

        if (this.create ? prop.required_on_post : prop.required) {
            validators.push(Validators.required);
        }

        // TODO: Check for missing constraints
        prop.validator_constraints.forEach( (constraint) => {
            switch (constraint.constraint_type) {
                case 'notblank':
                    validators.push(Validators.minLength(1));
                    break;
                case 'ip':
                    validators.push(Validators.pattern(/^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/));
                    break;
                case 'regex':
                    validators.push(Validators.pattern(constraint['pattern'].slice(1,-1)));
                    break;
            }
        });

        Object.keys(prop.orm_constraints).forEach( (constraintKey) => {
            let constraintValue;
            if ((constraintValue = prop.orm_constraints[constraintKey]) == null) {
                return;
            }
            switch (constraintKey) {
                case 'length':
                    validators.push(Validators.maxLength(constraintValue));
                    break;
                case 'options':
                    if (constraintValue.hasOwnProperty('unsigned') && constraintValue.unsigned) {
                        validators.push(Validators.min(0));
                    }
                    break;
            }
        });
        return validators;
    }

    private createLabel(name: string, divider: string = '-') : string {
        return name.replace(
            new RegExp('(?:^|' + divider +  ')(evid|ggw|gui|id|\\w)([^' + divider + ']*)', 'g'),
            (substring: string, p1: string, p2: string): string => {
                return p1.toUpperCase() + p2 + " ";
            }).trim();
    }

    private asFormArray(form : AbstractControl) : FormArray {
        return form as FormArray;
    }

    private getValueType(prop: EntityProperty) : string {
        return prop.type.collection ? prop.type.collection_value_type.builtin_type : prop.type.builtin_type;
    }
    private getValueUri(prop: EntityProperty) : string {
        return prop.type.collection ? prop.type.collection_value_type.resource_uri : prop.type.resource_uri;
    }

    private isRelation(prop: EntityProperty) {
        return this.getValueType(prop) == 'object' && this.getValueUri(prop) != null;
    }

    private getEntityData(prop: EntityProperty, uri: string = null) : Object {
        let entityObj : Object = {};
        let entityObjFromEmbedded : Object = {};
        let entityObjFromLinks : Object = {};
        if (this.isRelation(prop)) {
            // Check for embedded and links
            if (this.entityData.data['_embedded'] && this.entityData.data['_embedded'].hasOwnProperty(prop.name)) {
                entityObjFromEmbedded = this.entityData.data['_embedded'][prop.name];
            }
            if (this.entityData.data['_links'] && this.entityData.data['_links'].hasOwnProperty(prop.name)) {
                entityObjFromLinks = this.entityData.data['_links'][prop.name];
            }
            // Search in collections if URI is given
            if (uri && prop.type.collection) {
                if (![entityObjFromEmbedded, entityObjFromLinks].some((curEntityObj: Object): boolean => {
                    if (!(curEntityObj instanceof Array)) return false;
                    return curEntityObj.some((subEntityObj): boolean => {
                        if (
                            (subEntityObj['href'] && subEntityObj['href'] == uri) ||
                            (subEntityObj['_links'] && subEntityObj['_links']['self']['href'] == uri) ||
                            (subEntityObj['id'] && (() => {
                                let m = uri.match(new RegExp('^.+/' + subEntityObj['id'] + '$'));
                                return m && m.length == 1;
                            })())
                        ) {
                            entityObj = subEntityObj;
                            // break
                            return true;
                        }
                        return false;
                    });
                })) {
                    // not found in collections - choose given URI
                    entityObj = {'href': uri};
                }
            } else { // Return entity or collection (if no URI is given)
                 Object.assign(entityObj, entityObjFromLinks, entityObjFromEmbedded);
            }

            return entityObj;
        } else { // is primitive or no resource
            return this.entityData.data.hasOwnProperty(prop.name) ? this.entityData.data[prop.name] : null;
        }
    }


    public createLabelFromData(data: Object|string, prop: EntityProperty = null) : string {
        if  (typeof data == 'string' && prop) {
            let dataExtracted = this.getEntityData(prop, data);
            if (!dataExtracted) {
                return data;
            }
            data = dataExtracted;
        }
        if (!data) {
            return '- not set -';
        }
        let label: string = null;
        for (let property of ['display_label', 'label', 'name', 'ref_label', 'href', 'role', 'key']) {
            if (data.hasOwnProperty(property)) {
                label = data[property];
                break;
            }
        }
        if (label == null) {
            if (data['id']) {
                label = 'ID ' + data['id'];
            }
        }
        return label;
    }

    private createExplorerItemModel(resourceUri: string) : ExplorerItem {
        const label = this.createLabel(resourceUri);

        return {
            label: label,
            value: resourceUri,
            expanded: false,
            allowedActions: []
        };
    }

    private createExplorerItemEntity(entity: Object) : ExplorerItem {
        let label: string = this.createLabelFromData(entity);

        if (!label) {
            label = this.activeExplorerItemModel.label.replace(/s$/, '') + " " +
                entity.hasOwnProperty('id') ? entity['id'] : '(ID unknown)';
        }

        const explorerItem: ExplorerItem = {
            label: label,
            // description: 'ID ' + entity.id,
            value: entity,
            expanded: false,
            allowedActions: []
        };

        return explorerItem;
    }

    private updateEntityExplorer() {
        // const schema: EntitySchema = this.modelSchemas[resourceUri];
        const self = this;
        this.fetchResources((resource: any) => {
            self.explorerItemsEntities = [];
            // self. = [];
            if (resource) {
                resource._embedded.item.forEach((entity) => {
                    const explorerItem = self.createExplorerItemEntity(entity);
                    self.explorerItemsEntities.push(explorerItem);
                    if (Number.parseInt(entity.id) == self.selectedEntityId) {
                        // self.selectedEntity = entity;
                        setTimeout(() => {
                            self.onActivateItemEntity({
                                item: explorerItem,
                                nodeLevel: 0,
                                isCustom: false,
                                processed: false
                            });
                        });
                    }
                });
                self.updateTitleBreadcrumbs();
            }
        });
    }
    private fetchResources(callback: (resource: any) => void, item: boolean = false) {
        let resourceService: ResourceHandler;
        if (item) {
            this.resourceServiceItem = resourceService = this.genericResourceService.findServiceForResource(
                this.entitySchemaService.BASE +
                '/' + this.activeExplorerItemModel.value +
                '/' + this.activeExplorerItemEntity.value.id
            );
        } else { // collection
            this.resourceServiceColl = resourceService = this.genericResourceService.findServiceForResource(
                this.entitySchemaService.BASE +
                '/' + this.activeExplorerItemModel.value
            );
        }
        resourceService.getCurrent().subscribe(callback);
        resourceService.load();
    }

    onAddOrCreate($event) {
        if (!this.selectedSchema.operations.collection.includes("post")) {
            this.notificationService.error(
                'POST method is not supported!',
                'Add POST method to collection operations.'
            );
            return;
        }
        if (this.form) {
            this.form.reset();
        }
        this.resetEntity();
        this.create = true;
        this.router.navigate(['model-editor', this.activeExplorerItemModel.value, 'create']);
        this.createEntityDataAndForm();
    }

    private updateTitleBreadcrumbs() {
        this.resetTitleBreadcrumbs();
        if (this.activeExplorerItemModel) {
            this.titleBreadcrumbs.push(this.activeExplorerItemModel.label);
        }
        if (this.activeExplorerItemEntity) {
            this.titleBreadcrumbs.push(this.activeExplorerItemEntity.label);
        } else if (this.create) {
            this.titleBreadcrumbs.push('Create new item');
        }
    }

    private resetActiveExplorerItemModel() {
        this.activeExplorerItemModel = null;
    }
    private resetActiveExplorerItemEntity() {
        this.activeExplorerItemEntity = null;
    }

    private resetTitleBreadcrumbs() {
        this.titleBreadcrumbs = [...defaultBreadcrumb];
    }

    addField(prop: EntityProperty, formArray: FormArray) {
        formArray.push(this.createFormControl(prop, undefined));
    }

    addToRelation(prop: EntityProperty) {
        let control = this.form.get(prop.name);
        const compRef = this.modalService.open(AddToRelationComponent);
        const component: AddToRelationComponent = compRef.componentInstance;
        component.prop = prop;
        component.control = control;
        component.createLabelFromData = this.createLabelFromData;
        component.createHoverDivForObject = this.createHoverDivForObject;
        component.onAddOrChange = (selectedEntities: Set<Object>) => {
            if (!prop.type.collection) {
                const entity: Object = selectedEntities.values().next().value;
                if (!this.entityData.data['_embedded']) this.entityData.data['_embedded'] = {};
                this.entityData.data['_embedded'][prop.name] = entity;
                if (this.entityData.data['_links'] && this.entityData.data['_links'][prop.name]) delete this.entityData.data['_links'][prop.name];
                control.setValue(entity['_links']['self']['href']);
                control.markAsDirty();
                control.markAsTouched();
            } else {
                selectedEntities.forEach( (entity: Object) => {
                    if (!this.entityData.data['_embedded']) this.entityData.data['_embedded'] = {};
                    if (!this.entityData.data['_embedded'].hasOwnProperty(prop.name)) this.entityData.data['_embedded'][prop.name] = [];
                    (this.entityData.data['_embedded'][prop.name] as Array<any>).push(entity);
                    const formControl: FormControl = this.createFormControl(prop, entity['_links']['self']['href']);
                    (control as FormArray).push(formControl);
                    control.markAsDirty();
                    control.markAsTouched();
                });
            }
            // this.form.markAsDirty();
            compRef.close();
        };
    }

    createFormControl(prop: EntityProperty, value: any) {
        const disabled: boolean = !this.user.roles.includes(ROLE.MODEL_EDIT) ||
            (prop.type.builtin_type == 'bool' && !(this.create && prop.constructor_parameter || prop.writable));
        if (prop.is_date && value) {
            value = value.split('T')[0];
        }
        return new FormControl({
            value: this.isRelation(prop) && value !== null && (typeof value === "object") && value['href'] ? value['href'] : value,
            disabled: disabled
        }, this.selectValidators(prop));
    }

    onSubmit(event) {
        const form: FormGroup = lodash.cloneDeep(this.form);

        this.objectKeys(form.controls).forEach((key) => {
            if (form.controls[key].untouched || form.controls[key].value === undefined) {
                form.removeControl(key);
            }
        });

        let resourceService: ResourceHandler = this.create ? this.resourceServiceColl : this.resourceServiceItem;
        resourceService.save(form.value, this.create).subscribe((result) => {
            if (this.activeExplorerItemModel.value === 'users') {
                this.userService.refresh();
            }
            this.create = false;
            this.createEntityDataAndForm(result);
        });
    }

    deleteModel(event) {
        this.resourceServiceItem.delete();
        this.resetEntity();
    }

    createHoverDivForTreeNode(nodeInfo: TreeNodeInfo): HTMLDivElement {
        // return this.createHoverDivForObject(nodeInfo.item.value);
        const div: HTMLDivElement = document.createElement('div');
        const pre: HTMLPreElement = document.createElement('pre');
        pre.innerHTML = JSON.stringify(nodeInfo.item.value, null, 3);
        div.appendChild(pre);
        return div;

    }

    private createHoverDivForObject(entity: Object): HTMLDivElement {
        const div: HTMLDivElement = document.createElement('div');
        const pre: HTMLPreElement = document.createElement('pre');
        pre.innerHTML = JSON.stringify(entity, null, 3);
        div.appendChild(pre);
        return div;
    }

    private updateFile($event, prop: EntityProperty) {
        const fileReader = new FileReader();
        if ($event.target.files.length !== 1)
            return;
        const fileInfo = $event.target.files[0];
        fileReader.onload = (fileEvent: any) => {
            const uploadTarget: any = fileEvent.target;
            const fileContent = `${encodeURI(uploadTarget.result)}`;
            let fileComp = this.form.get(prop.name);
            fileComp.setValue(fileContent);
            fileComp.markAsDirty(); fileComp.markAsTouched();
        };
        fileReader.readAsDataURL(fileInfo);
    }

    private removeFile(prop: EntityProperty) {
        let fileComp = this.form.get(prop.name);
        fileComp.setValue(null);
        fileComp.markAsDirty(); fileComp.markAsTouched();
    }

    private createFileName(prop: EntityProperty) : string {
        if (!this.form.get(prop.name).value) {
            return '- not set -';
        }
        if (prop.filename_property && this.form.get(prop.filename_property) && this.form.get(prop.filename_property).value) {
            return this.form.get(prop.filename_property).value;
        }
        if (prop.filename_original_property && this.form.get(prop.filename_original_property).value) {
            return '- derived from "' + this.form.get(prop.filename_original_property).value + '" -';
        }
        return '- no filename given -';
    }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/model-editor/components/model-editor.component.ts